import React from "react";
import {
  Pressable,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from "react-native";
import SecondaryButton from "../../../../common/native/SecondaryButton";
import TOKENS from "../styles/tokens";
import { TWebhookMappingRow } from "../types";
import { makeId } from "./mappingUtils";

type Props = {
  mappings: TWebhookMappingRow[];
  onChange: (next: TWebhookMappingRow[]) => void;
  onOpenSourceCatalog: (mappingId: string) => void;
  errorsByPath: Record<string, string[]>;
};

const GuidedMappingEditor: React.FC<Props> = ({
  mappings,
  onChange,
  onOpenSourceCatalog,
  errorsByPath,
}) => {
  const updateRow = (
    rowId: string,
    key: keyof TWebhookMappingRow,
    value: string | boolean,
  ) => {
    onChange(
      mappings.map((row) =>
        row.id === rowId ? { ...row, [key]: value } : row,
      ),
    );
  };

  const removeRow = (rowId: string) => {
    if (mappings.length <= 1) return;
    onChange(mappings.filter((row) => row.id !== rowId));
  };

  const addRow = () => {
    onChange([
      ...mappings,
      {
        id: makeId("mapping"),
        target_field: "",
        source_path: "",
        default_value: "",
        literal_value: "",
        include_if_none: false,
      },
    ]);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Guided Mapping</Text>
      {mappings.map((row, index) => {
        const targetErrors =
          errorsByPath[`field_mappings[${index}].target_field`] ?? [];
        const rowErrors = errorsByPath[`field_mappings[${index}]`] ?? [];

        return (
          <View key={row.id} style={styles.card}>
            <Text style={styles.cardTitle}>Mapping {index + 1}</Text>

            <Text style={styles.label}>Target Field *</Text>
            <TextInput
              style={styles.input}
              value={row.target_field}
              onChangeText={(value) => updateRow(row.id, "target_field", value)}
              placeholder="contact.first_name"
              autoCapitalize="none"
            />
            {targetErrors.map((error) => (
              <Text key={error} style={styles.errorText}>
                {error}
              </Text>
            ))}

            <Text style={styles.label}>Source Path</Text>
            <View style={styles.sourceRow}>
              <TextInput
                style={[styles.input, styles.sourceInput]}
                value={row.source_path}
                onChangeText={(value) =>
                  updateRow(row.id, "source_path", value)
                }
                placeholder="patient_record.first_name"
                autoCapitalize="none"
              />
              <Pressable
                style={styles.sourceButton}
                onPress={() => onOpenSourceCatalog(row.id)}
              >
                <Text style={styles.sourceButtonText}>Catalog</Text>
              </Pressable>
            </View>

            <Text style={styles.label}>Default Value</Text>
            <TextInput
              style={styles.input}
              value={row.default_value}
              onChangeText={(value) =>
                updateRow(row.id, "default_value", value)
              }
              placeholder="Fallback value"
              autoCapitalize="none"
            />

            <Text style={styles.label}>Literal Value</Text>
            <TextInput
              style={styles.input}
              value={row.literal_value}
              onChangeText={(value) =>
                updateRow(row.id, "literal_value", value)
              }
              placeholder="Static value"
              autoCapitalize="none"
            />

            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Include if value is null</Text>
              <Switch
                value={row.include_if_none}
                onValueChange={(value) =>
                  updateRow(row.id, "include_if_none", value)
                }
              />
            </View>

            {rowErrors.map((error) => (
              <Text key={error} style={styles.errorText}>
                {error}
              </Text>
            ))}

            <Pressable
              style={styles.removeButton}
              onPress={() => removeRow(row.id)}
              disabled={mappings.length <= 1}
            >
              <Text style={styles.removeButtonText}>Remove Mapping</Text>
            </Pressable>
          </View>
        );
      })}

      <SecondaryButton text="Add Mapping" onPress={addRow} fullWidth />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: TOKENS.spacing.md,
  },
  title: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.lg,
    lineHeight: TOKENS.lineHeight.lg,
    color: TOKENS.color.textPrimary,
  },
  card: {
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
    backgroundColor: TOKENS.color.surface,
  },
  cardTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.md,
    lineHeight: TOKENS.lineHeight.md,
    color: TOKENS.color.textPrimary,
    marginBottom: TOKENS.spacing.xs,
  },
  label: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
    color: TOKENS.color.textSecondary,
  },
  input: {
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.sm,
    minHeight: 44,
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    backgroundColor: TOKENS.color.surface,
  },
  sourceRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
    alignItems: "center",
  },
  sourceInput: {
    flex: 1,
  },
  sourceButton: {
    minHeight: 44,
    justifyContent: "center",
    borderWidth: 1,
    borderColor: TOKENS.color.borderStrong,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.sm,
  },
  sourceButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  switchRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 44,
  },
  switchLabel: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  errorText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.error,
  },
  removeButton: {
    marginTop: TOKENS.spacing.xs,
    minHeight: 44,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: TOKENS.color.error,
    borderRadius: TOKENS.radius.md,
  },
  removeButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.error,
  },
});

export default GuidedMappingEditor;
